import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  DataGridOnCheckedRowsArg,
  DataFetcherParams,
  FetchWatcher,
} from '@axiom/ui';
import {
  Icon,
  Flashy,
  CalendarIcon,
  DataGrid,
  AsyncConnector,
  DataGridHeader,
  Gutter,
  CondensedHeader,
  IconButton,
  Layout,
  LayoutItem,
  useApi,
  useClearApiCacheOnUnmount,
  Badge,
  Grid,
  GridRow,
  GridColumn,
  Tooltip,
  ProfileStatusBadge,
  profileStatusColors,
  PluralizeUtil,
} from '@axiom/ui';
import { PositionsConst } from '@axiom/const';
import { Link, useNavigate } from 'react-router';
import type { Candidate, EnvoyCandidateSearchFilters } from '@axiom/validation';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { CurrencyUtil } from '@axiom/utils';

import { UserApi } from '../../api/user-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { WindowUtil } from '../../utils/window-util';
import { NumberUtil } from '../../utils/number-util';
import { DateUtil } from '../../utils/date-util';
import { downloadTalentList } from '../../api/legacy-talent-api';
import { EnvUtil } from '../../utils/env-util';
import { encodeApiObjects } from '../../classes/legacy-api-helper';
import { AxiomForTalentUtil } from '../../utils/axiom-for-talent-util';
import { CandidateUtil } from '../../utils/candidate-util';
import { isSalesUser } from '../../utils/roles-util';
import { TalentOwnerUtil } from '../../utils/talent-owner-util';
import type { ReadCandidatesResponseType } from '../../api/candidate-api';
import { CandidateApi } from '../../api/candidate-api';
import { generateAddressDisplayName } from '../../redux/selectors/helpers';
import { CandidateAvailabilityModalStore } from '../../stores/candidate-availability-modal-store';
import { PreloadedUsersStore } from '../../stores/preloaded-users-store';
import { useAiSearch } from '../../providers/AiSearchContextProvider';
import { TalentBulkActions } from '../TalentBulkActions/TalentBulkActions';

type ColumnSortingType = Array<'asc' | 'desc' | null> | undefined;

export const sanitizeFilters = ({
  filters: inputFilters,
  page = 0,
  sort,
}: {
  filters: Record<string, unknown>;
  sort: DataFetcherParams['sort'] | null;
  page: DataFetcherParams['page'] | null;
}) => {
  const filters = { ...inputFilters };

  if (filters.skills) {
    filters['experiences.tags'] = filters.skills;
    delete filters.skills;
  }

  ['yearsOfExperience', 'weeklyAvailability', 'compensation'].forEach(
    propName => {
      const propSlice = filters[propName] as
        | {
            start?: unknown;
            end?: unknown;
          }
        | undefined;
      if (
        !Number.isInteger(propSlice?.start) &&
        !Number.isInteger(propSlice?.end)
      ) {
        delete filters[propName];
      }
    }
  );

  Object.keys(filters).forEach(propName => {
    if ((filters[propName] as unknown[] | string | undefined)?.length === 0) {
      delete filters[propName];
    }
  });

  return {
    search: filters.search || undefined,
    filters,
    page: page || undefined,
    sort: sort
      ? (Object.keys(sort).reduce<null | string>((acc, key) => {
          if (sort[key] && !acc) {
            acc = key;
          }

          return acc;
        }, null) ?? undefined)
      : undefined,
    order: sort ? (Object.values(sort).find(Boolean) ?? undefined) : undefined,
  };
};
export type CandidatesListProps = {
  filters: EnvoyCandidateSearchFilters;
};

export const CandidatesList = ({ filters }: CandidatesListProps) => {
  const { isActive: aiIsActive, results: aiResults } = useAiSearch();
  const columnSortingAsc: ColumnSortingType = aiIsActive
    ? undefined
    : ['asc', 'desc', null];
  const columnSortingDesc: ColumnSortingType = aiIsActive
    ? undefined
    : ['desc', 'asc', null];

  // Stable key for filter content - triggers GridConnector recreation when values change
  const filtersKey = JSON.stringify(filters);

  // Track the stringified filters to detect actual value changes for checked rows
  const prevFiltersStringRef = useRef<string>('');

  const [resultCount, setResultCount] = useState<number>(0);

  const [{ data: taxonomy }, { data: user }] = useApi(
    TaxonomyApi.readTaxonomy(),
    UserApi.readSessionUser()
  );
  const userList = useSelector(state =>
    PreloadedUsersStore.selectDataForById(state)
  );
  const [checkedRows, setCheckedRows] = useState<DataGridOnCheckedRowsArg>({
    allRowsChecked: false,
    checkedRows: [],
  });
  const checkedRowsRef = useRef(checkedRows);
  checkedRowsRef.current = checkedRows;

  const allLoadedRowsRef = useRef<ReadCandidatesResponseType['data']>([]);

  const navigate = useNavigate();

  const { practiceAreas } = taxonomy;

  // Clear the endpoint cache on unmount to prevent stale cached requests
  useClearApiCacheOnUnmount(CandidateApi, '/candidates/search');

  // Reset checked rows when filter values actually change
  useEffect(() => {
    // Skip on first render
    if (prevFiltersStringRef.current === '') {
      prevFiltersStringRef.current = filtersKey;
      return;
    }

    // If filters changed, reset checked rows
    if (prevFiltersStringRef.current !== filtersKey) {
      prevFiltersStringRef.current = filtersKey;
      setCheckedRows({
        allRowsChecked: false,
        checkedRows: [],
      });
    }
  }, [filtersKey]);

  // Recreate GridConnector when filter values change to trigger DataGrid refetch
  const GridConnector = useMemo(() => {
    allLoadedRowsRef.current = [];
    return new AsyncConnector<ReadCandidatesResponseType>({
      getData: args => {
        if (aiIsActive) {
          const data = aiResults ?? {
            data: [],
            meta: { resultCount: 0, currentPage: 0, pageCount: 0 },
          };
          return {
            hasResolved: () => true,
            getData: () => data,
            getError: () => null,
            hasError: () => false,
            toPromise: async () => {
              const result = await Promise.resolve(data);
              return result;
            },
            refetch: async () => {
              const result = await Promise.resolve(data);
              return result;
            },
            listen: () => ({ unsubscribe: () => {} }),
            listenForInvalidate: () => ({ unsubscribe: () => {} }),
          } as unknown as FetchWatcher<ReadCandidatesResponseType>;
        }

        const sendFilters = sanitizeFilters({
          filters: { ...filters },
          ...args,
        });

        return CandidateApi.readCandidates({
          ...sendFilters,
          filters:
            sendFilters.filters as unknown as EnvoyCandidateSearchFilters,
        });
      },
      parseData: (results: ReadCandidatesResponseType) => {
        // The grid's row checkboxes match checked rows by reference, so the
        // ref must hold the exact objects handed to the grid, not results.data.
        const gridRows = results.data.map(item => ({
          ...item,
          compensation: CurrencyUtil.getFormattedCurrency({
            value: item.compensation ?? null,
            currencyCode: item.currencyType,
            removeCentsIfZero: false,
          }),
        }));
        // AI mode is single-page by design — always reset. Normal mode resets
        // only on page 1 so subsequent pages accumulate.
        if (aiIsActive || results.meta.currentPage === 1) {
          allLoadedRowsRef.current = [];
        }
        allLoadedRowsRef.current = [...allLoadedRowsRef.current, ...gridRows];
        // AI search caps server-side at 100 results while meta.resultCount
        // reflects the total DB match count; display the retrieved count so
        // the header matches the rows actually rendered in the grid.
        setResultCount(
          aiIsActive
            ? allLoadedRowsRef.current.length
            : results.meta.resultCount
        );
        if (checkedRowsRef.current.allRowsChecked) {
          setCheckedRows({
            allRowsChecked: true,
            checkedRows: [...allLoadedRowsRef.current],
          });
        }

        return {
          // In AI mode, once a result has landed (success or failure), force
          // currentPage to match the hard-coded totalPages so DataGridInfinite
          // sends a bounded lastRow and ag-grid finalizes. Without aiResults
          // (search still in flight), leave currentPage alone so the watcher's
          // meta.currentPage=0 keeps the grid in its "Fetching data..." state.
          currentPage: aiIsActive && aiResults ? 1 : results.meta.currentPage,
          totalPages: aiIsActive ? 1 : results.meta.pageCount,
          data: gridRows,
        };
      },
      pageSize: aiIsActive ? aiResults?.data.length || 100 : 20,
      onUnmount: () => {
        CandidateApi.clearReadCandidates();
      },
    });
  }, [filtersKey, aiIsActive, aiResults]);

  const flaggedTalentCellRender = ({ data }: { data: Candidate }) => {
    return data.isFlagged ? (
      <Badge background="red" name={`${data.id}-flagged-badge`}>
        Flagged
      </Badge>
    ) : (
      '-'
    );
  };

  const flaggedTalentDisplayName = () => (
    <Grid>
      <GridRow columns={2} verticalAlign="middle">
        <GridColumn>Flagged</GridColumn>
        <GridColumn>
          <Tooltip>
            Please reach out to the Talent Owner before proceeding.
          </Tooltip>
        </GridColumn>
      </GridRow>
    </Grid>
  );

  const isProfileStatusCellRender = ({ data }: { data: Candidate }) => {
    return data.profileStatus ? (
      <ProfileStatusBadge
        profileStatus={data.profileStatus}
        name={`${data.id}-profile-status-badge`}
      />
    ) : (
      '-'
    );
  };

  const calculatedLastNameCell = ({
    data: candidateData,
  }: {
    data: Candidate;
  }) => {
    return candidateData.calculatedDisplayName;
  };

  const currentAvailabilityCell = ({
    data: candidateData,
  }: {
    data: Candidate;
  }) => {
    return NumberUtil.formatAsHours(candidateData.weeklyAvailability);
  };

  const avgClientRecommendCell = ({
    data: candidateData,
  }: {
    data: Candidate;
  }) => {
    if (
      !candidateData.candidateReputation?.averageClientRecommendScore ||
      !candidateData.candidateReputation.numberOfRespondents
    ) {
      return '';
    }

    return (
      <Link
        data-test={`${candidateData.id}-avg-client-recommend-link`}
        to={`/talent-detail/${candidateData.id}/feedback`}
        target={candidateData.id}
      >
        {candidateData.candidateReputation.averageClientRecommendScore} (
        {candidateData.candidateReputation.numberOfRespondents}{' '}
        {PluralizeUtil({
          text: 'response',
          count: candidateData.candidateReputation.numberOfRespondents,
        })}
        )
      </Link>
    );
  };

  const rollOffDateCell = ({ data: candidateData }: { data: Candidate }) => {
    const endDate = candidateData.soonestEngagementEndDate;

    return (
      <Layout position="middle" horizontalGutter="4px">
        {candidateData.soonestEngagementEndDateStatus ===
          PositionsConst.EndDateStatuses.Confirmed && (
          <Gutter top="8px">
            <span
              data-test="CONFIRMED_END_DATE_ICON"
              title={PositionsConst.EndDateStatuses.Confirmed}
            >
              <CalendarIcon withCheck />
            </span>
          </Gutter>
        )}
        <LayoutItem>{DateUtil.displayDate(endDate)}</LayoutItem>
      </Layout>
    );
  };

  const practiceAreaCell = ({ data }: { data: Candidate }) => {
    return (
      data.primaryPracticeArea &&
      practiceAreas[data.primaryPracticeArea]?.fullName
    );
  };

  const residenceCell = ({ data: candidateData }: { data: Candidate }) => {
    return generateAddressDisplayName(candidateData);
  };

  const talentOwnerCell = ({ data }: { data: Candidate }) => {
    if (
      TalentOwnerUtil.isHiddenFromSales({
        isEnvoySalesUser: isSalesUser(user),
        profileStatus: data.profileStatus,
      })
    ) {
      return 'Hidden';
    }

    return data.ownerUserId && userList[data.ownerUserId]?.fullName;
  };

  const renderBulkActions = () => {
    if (!checkedRows.allRowsChecked && checkedRows.checkedRows.length === 0) {
      return null;
    }

    const rows =
      checkedRows.allRowsChecked && checkedRows.checkedRows.length === 0
        ? allLoadedRowsRef.current
        : (checkedRows.checkedRows as Candidate[]);

    return (
      <TalentBulkActions
        rows={rows}
        checkedCount={rows.length}
        user={user}
        onExportCsv={async () => {
          const search = checkedRows.allRowsChecked
            ? sanitizeFilters({
                filters,
                sort: null,
                page: null,
              })
            : {
                filters: {
                  ids: rows.map(row => row.id),
                },
              };
          await downloadTalentList({ request: search });

          const uri = `${
            EnvUtil.apiEnvoyUrl
          }/downloads/candidates?${encodeApiObjects({
            request: search,
          })}`;
          window.location.href = uri;
        }}
      />
    );
  };

  const pinnedOptionsCell = ({ data: candidateData }: { data: Candidate }) => {
    const bioDisposition = CandidateUtil.getBioDisposition(candidateData);
    return (
      <Layout position="center middle" horizontalGutter="8px">
        <LayoutItem borderRight="1px solid contentBreak">
          <Gutter right="8px">
            <IconButton
              title="Show availability calculation"
              onClick={async () =>
                CandidateAvailabilityModalStore.openModal(candidateData.id)
              }
              pattern="secondary"
              variation="minimal"
              icon="clock"
              name="AVAILABILITY_MODAL_CLOCK_ICON"
            />
          </Gutter>
        </LayoutItem>
        <LayoutItem borderRight="1px solid contentBreak">
          <Gutter right="8px">
            <IconButton
              icon="pop-out"
              pattern="secondary"
              variation="minimal"
              name="OPEN_IN_NEW_TAB_BUTTON"
              toTab={`/talent-detail/${candidateData.id}`}
              title={`Open "${candidateData.calculatedDisplayName}" in new tab`}
            />
          </Gutter>
        </LayoutItem>
        <LayoutItem borderRight="1px solid contentBreak">
          <Gutter right="8px">
            {bioDisposition === 'skip' ? (
              <Gutter horizontal="8px">
                <Flashy color="textDisabled" name="DISABLED_BIO_AVATAR">
                  <Icon name="avatar" />
                </Flashy>
              </Gutter>
            ) : (
              <IconButton
                icon="avatar"
                pattern="secondary"
                variation="minimal"
                name="OPEN_BIO_BUTTON"
                title={`Open ${
                  bioDisposition === 'named' ? 'Bio' : 'Anonymous Bio'
                }`}
                onClick={() => {
                  if (bioDisposition === 'named') {
                    AxiomForTalentUtil.openBio(candidateData.id);
                  } else {
                    AxiomForTalentUtil.openAnonymousBio(candidateData.id);
                  }
                }}
              />
            )}
          </Gutter>
        </LayoutItem>
        <LayoutItem>
          <IconButton
            icon="tableau"
            pattern="secondary"
            variation="minimal"
            name="OPEN_TABLEAU_BUTTON"
            toTab={`${EnvUtil.tableauSimilarTalentUrl}?${qs.stringify({
              TNP: candidateData.calculatedDisplayName,
            })}`}
            title="Open Tableau to find similar talent"
          />
        </LayoutItem>
      </Layout>
    );
  };

  const isPriorityTalentCellRender = ({ data }: { data: Candidate }) =>
    data.isPriorityTalent ? (
      <Badge background="forest green" name={`${data.id}-priority-badge`}>
        Priority
      </Badge>
    ) : (
      '-'
    );

  const isPriorityTalentDisplayName = () => (
    <Grid>
      <GridRow columns={2} verticalAlign="middle">
        <GridColumn>Priority</GridColumn>
        <GridColumn>
          <Tooltip>
            Priority Talent have been on the Beach for over 6 weeks (2 weeks for
            Asia) or on the Waitlist for over 12 weeks (8 weeks for UK, 4 weeks
            for Asia) and are available for opportunities. They can be priced at
            lower margins to give cost savings to the client.
          </Tooltip>
        </GridColumn>
      </GridRow>
    </Grid>
  );

  return (
    <>
      <Gutter vertical="8px">
        <CondensedHeader name="TALENT_COUNT">
          {resultCount} Talent
        </CondensedHeader>
      </Gutter>
      <DataGrid<ReadCandidatesResponseType>
        key={aiIsActive ? `ai-search-${String(!!aiResults)}` : 'candidate-list'}
        name="candidate-list-grid"
        displayMode="infinite"
        dataSource={GridConnector}
        suppressRowClickForColumns={['candidateReputation']}
        onSelectionChanged={rows => {
          if (rows.length === 1) {
            const rowId = rows[0]?.id as string;
            void navigate(WindowUtil.appendQueryParams(`/talent/${rowId}`));
          }
        }}
        rowsChecked={checkedRows}
        onCheckboxGroupChanged={rows => {
          setCheckedRows(rows);
        }}
        calculateRowBadge={(rowData: Partial<Candidate>) => {
          const status = rowData.profileStatus;
          return status ? profileStatusColors[status].colorCode : '';
        }}
      >
        <DataGridHeader<Candidate>
          name="flaggedTalent"
          pinned="left"
          width={124}
          displayName={flaggedTalentDisplayName}
          cellRender={flaggedTalentCellRender}
        />
        <DataGridHeader
          name="calculatedLastName"
          displayName="Talent"
          pinned="left"
          sortingOrder={columnSortingAsc}
          cellRender={calculatedLastNameCell}
        />
        <DataGridHeader<Candidate>
          name="profileStatus"
          displayName="Profile status"
          sortingOrder={columnSortingAsc}
          cellRender={isProfileStatusCellRender}
        />
        <DataGridHeader
          name="compensation"
          width={260}
          displayName="Total annual compensation"
          sortingOrder={columnSortingAsc}
        />
        <DataGridHeader
          name="weeklyAvailability"
          displayName="Current availability"
          sortingOrder={columnSortingAsc}
          cellRender={currentAvailabilityCell}
        />
        <DataGridHeader
          name="soonestEngagementEndDate"
          displayName="Roll off date"
          sortingOrder={columnSortingAsc}
          cellRender={rollOffDateCell}
        />
        <DataGridHeader
          name="primaryPracticeArea"
          displayName="Practice area"
          cellRender={practiceAreaCell}
        />
        <DataGridHeader
          name="candidateReputation"
          displayName="Avg. Client Recommend"
          cellRender={avgClientRecommendCell}
        />
        <DataGridHeader
          name="addressCity"
          displayName="Residence"
          sortingOrder={columnSortingAsc}
          cellRender={residenceCell}
        />
        <DataGridHeader
          name="ownerUser.lastName"
          displayName="Talent owner"
          sortingOrder={columnSortingAsc}
          cellRender={talentOwnerCell}
        />
        <DataGridHeader
          name="isPriorityTalent"
          width={184}
          displayName={isPriorityTalentDisplayName}
          sortingOrder={columnSortingDesc}
          cellRender={isPriorityTalentCellRender}
        />
        <DataGridHeader
          name="pinner-options"
          pinned="right"
          width={200}
          displayName={() => renderBulkActions()}
          cellRender={pinnedOptionsCell}
        />
      </DataGrid>
    </>
  );
};
